import React from "react";
import { Clock, MapPin } from "lucide-react";

import { AvaliacaoEstrelas } from "../avaliacaoestrelas/AvaliacaoEstrelas";
import { DiaSemana, ILojaPublica } from "shared/interfaces/ILojaPublica";

import styles from "./LojaHeader.module.scss";

interface LojaHeaderProps {
  loja: ILojaPublica;
}

const DIAS: Record<string, DiaSemana> = {
  Mon: "MON",
  Tue: "TUE",
  Wed: "WED",
  Thu: "THU",
  Fri: "FRI",
  Sat: "SAT",
  Sun: "SUN",
};

/* ================================
   ABERTA / FECHADA
   Horários são de America/Sao_Paulo, independente do fuso do aparelho.
================================ */

function situacaoAtual(horarios: ILojaPublica["horarios"]) {
  const partes = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/Sao_Paulo",
    weekday: "short",
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
  }).formatToParts(new Date());

  const parte = (tipo: string) =>
    partes.find((p) => p.type === tipo)?.value ?? "";

  const horarioHoje = horarios[DIAS[parte("weekday")]];

  if (!horarioHoje) {
    return { aberta: false, texto: "Fechada hoje" };
  }

  const agora = `${parte("hour")}:${parte("minute")}`;
  const { openingTime, closingTime } = horarioHoje;

  // Fechamento depois da meia-noite (ex.: 18:00 às 02:00).
  const aberta =
    closingTime > openingTime
      ? agora >= openingTime && agora < closingTime
      : agora >= openingTime || agora < closingTime;

  if (aberta) {
    return { aberta: true, texto: `Aberta agora · fecha às ${closingTime}` };
  }

  if (agora < openingTime) {
    return { aberta: false, texto: `Fechada · abre às ${openingTime}` };
  }

  return { aberta: false, texto: "Fechada" };
}

/* ================================
   ENDEREÇO
   "Rua X, 100 - Bairro, Cidade/UF", pulando o que vier vazio.
================================ */

function enderecoCompleto({
  rua,
  numero,
  bairro,
  cidade,
  estado,
}: ILojaPublica["endereco"]) {
  const logradouro = [rua, numero].filter(Boolean).join(", ");
  const localidade = [cidade, estado].filter(Boolean).join("/");
  const local = [bairro, localidade].filter(Boolean).join(", ");
  return [logradouro, local].filter(Boolean).join(" - ");
}

export const LojaHeader: React.FC<LojaHeaderProps> = ({ loja }) => {
  const { media, total } = loja.avaliacao;
  const situacao = situacaoAtual(loja.horarios);
  const endereco = enderecoCompleto(loja.endereco);

  return (
    <section className={styles.container}>
      <div className={styles.topo}>
        <img
          src={loja.logo || "/imagens/semloja.png"}
          alt={`Logo ${loja.nome}`}
          className={styles.logo}
          onError={(e) => {
            e.currentTarget.src = "/imagens/semloja.png";
          }}
        />

        <div className={styles.identificacao}>
          <h1 className={styles.nome}>{loja.nome}</h1>

          <span className={styles.segmento}>{loja.categoria.nome}</span>

          <div className={styles.avaliacao}>
            {media === null ? (
              <span className={styles.semAvaliacao}>Sem avaliações ainda</span>
            ) : (
              <>
                <AvaliacaoEstrelas rating={media} size={16} />
                <strong className={styles.media}>
                  {media.toLocaleString("pt-BR", {
                    minimumFractionDigits: 1,
                    maximumFractionDigits: 1,
                  })}
                </strong>
                <span className={styles.total}>
                  ({total} {total === 1 ? "avaliação" : "avaliações"})
                </span>
              </>
            )}
          </div>
        </div>
      </div>

      {loja.descricao && (
        <p className={styles.descricao}>{loja.descricao}</p>
      )}

      <div className={styles.detalhes}>
        {endereco && (
          <span className={styles.detalhe}>
            <MapPin size={16} />
            {endereco}
          </span>
        )}

        <span
          className={`${styles.detalhe} ${
            situacao.aberta ? styles.aberta : styles.fechada
          }`}
        >
          <Clock size={16} />
          {situacao.texto}
        </span>
      </div>
    </section>
  );
};
